import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  updateDoc,
} from "firebase/firestore";
import { db, auth } from "../lib/firebase";
import { StrategicAssessment, RedFlagRisk, EffortBreakdown, ExecutiveBrief } from "../types";

/**
 * Subscribes to the latest strategic assessment for an opportunity
 */
export function subscribeToLatestAssessment(
  opportunityId: string,
  callback: (assessment: StrategicAssessment | null) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "strategic_assessments");
  const q = query(colRef, orderBy("generatedAt", "desc"), limit(1));

  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty || !snapshot.docs[0]) {
        callback(null);
        return;
      }

      const docSnap = snapshot.docs[0];
      const data = docSnap.data();

      const assessment: StrategicAssessment = {
        id: docSnap.id,
        opportunityId,
        overallFitScore: typeof data.overallFitScore === "number" ? data.overallFitScore : 0,
        recommendation: data.recommendation || "caution",
        recommendationRationale: data.recommendationRationale || "",
        pillars: Array.isArray(data.pillars) ? data.pillars : [],
        redFlags: Array.isArray(data.redFlags) ? data.redFlags : [],
        executiveBrief: (typeof data.executiveBrief === "object" && data.executiveBrief !== null
          ? data.executiveBrief
          : {
              clientOverview: "",
              corePainPoints: [],
              recommendedWinTheme: "",
              winStrategySummary: "",
              effortEstimate: {
                mode: "ai_estimate",
                writingHours: 0,
                smeReviewHours: 0,
                managementHours: 0,
                totalHours: 0,
              },
              keyDifferentiators: [],
              topRisksSummary: "",
            }) as ExecutiveBrief,
        modelUsed: data.modelUsed || "",
        generatedAt: data.generatedAt || new Date().toISOString(),
        generatedBy: data.generatedBy || "",
        generatedByEmail: data.generatedByEmail || "",
        baseRequirementCount:
          typeof data.baseRequirementCount === "number" ? data.baseRequirementCount : 0,
        baseExtractionJobId: data.baseExtractionJobId,
        userNotes: data.userNotes,
      };

      callback(assessment);
    },
    (err) => {
      console.error(`Error subscribing to strategic assessments for ${opportunityId}:`, err);
      if (onError) onError(err);
    },
  );
}

/**
 * Invokes the phq_assessOpportunity Cloud Function
 */
export async function runStrategicAssessment(opportunityId: string): Promise<StrategicAssessment> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("You must be logged in to run the strategic assessment.");
  }
  const token = await currentUser.getIdToken();

  const baseUrl =
    import.meta.env.VITE_FUNCTIONS_BASE_URL ||
    "https://us-central1-in-house-dev-mlc.cloudfunctions.net";
  const url = `${baseUrl.replace(/\/$/, "")}/phq_assessOpportunity`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ opportunityId }),
  });

  if (!response.ok) {
    let errorMessage = `Failed to generate assessment (${response.status})`;
    try {
      const errorData = await response.json();
      if (errorData.error) errorMessage = errorData.error;
    } catch {
      // ignore
    }
    throw new Error(errorMessage);
  }

  const result = await response.json();
  return result.assessment as StrategicAssessment;
}

/**
 * Toggles acknowledgment for a specific red-flag risk on an assessment document
 */
export async function toggleRiskAcknowledgment(
  opportunityId: string,
  assessmentId: string,
  riskId: string,
  acknowledged: boolean,
): Promise<void> {
  const assessRef = doc(db, "opportunities", opportunityId, "strategic_assessments", assessmentId);
  const snap = await getDoc(assessRef);
  if (!snap.exists()) {
    throw new Error(`Assessment ${assessmentId} does not exist.`);
  }

  const data = snap.data();
  const redFlags: RedFlagRisk[] = Array.isArray(data.redFlags) ? data.redFlags : [];
  const currentUser = auth.currentUser;
  const userEmail = currentUser?.email || "User";

  const updatedFlags = redFlags.map((risk) => {
    if (risk.id !== riskId) return risk;
    return {
      ...risk,
      acknowledged,
      acknowledgedBy: acknowledged ? userEmail : undefined,
      acknowledgedAt: acknowledged ? new Date().toISOString() : undefined,
    };
  });

  await updateDoc(assessRef, { redFlags: updatedFlags });
}

/**
 * Updates manual effort hours override on executive brief
 */
export async function updateExecutiveEffortOverride(
  opportunityId: string,
  assessmentId: string,
  effort: EffortBreakdown,
): Promise<void> {
  const assessRef = doc(db, "opportunities", opportunityId, "strategic_assessments", assessmentId);
  const snap = await getDoc(assessRef);
  if (!snap.exists()) {
    throw new Error(`Assessment ${assessmentId} does not exist.`);
  }

  const data = snap.data();
  const currentBrief = (data.executiveBrief || {}) as ExecutiveBrief;

  await updateDoc(assessRef, {
    executiveBrief: {
      ...currentBrief,
      effortEstimate: effort,
    },
  });
}

/**
 * Saves human strategic notes on assessment record
 */
export async function saveAssessmentUserNotes(
  opportunityId: string,
  assessmentId: string,
  notes: string,
): Promise<void> {
  const assessRef = doc(db, "opportunities", opportunityId, "strategic_assessments", assessmentId);
  await updateDoc(assessRef, {
    userNotes: notes,
  });
}
